import { segmentGraphemes } from "@/lib/graphemes";
import {
  type AsciiFont,
  BLOCK_GLYPHS,
  SLIM_GLYPHS,
  SMALL_GLYPHS,
} from "@/lib/tool-kit/text/ascii-art-fonts";

/**
 * Text → multi-line ASCII banner art.
 *
 * Each input line becomes one banner: its characters are looked up in the font and their
 * rows laid side by side with the chosen spacing. Banners are separated by a blank line,
 * and trailing spaces are trimmed so the result pastes cleanly into code comments.
 */

export type AsciiFontId = "block" | "shadow" | "slim" | "small";

export const ASCII_FONT_IDS: readonly AsciiFontId[] = [
  "block",
  "shadow",
  "slim",
  "small",
];

/** Fonts drawn from the block bitmap, where the fill character applies. */
export const FILLED_FONTS: ReadonlySet<AsciiFontId> = new Set([
  "block",
  "shadow",
]);

export const MAX_SPACING = 3;
/** Longer input is cut here — a banner this long is already hundreds of columns wide. */
export const MAX_INPUT_CHARACTERS = 500;
/** The classic terminal width; wider art wraps in many places. */
export const TERMINAL_WIDTH = 80;

export interface AsciiArtOptions {
  font: AsciiFontId;
  /** Columns of space between letters, 0 to `MAX_SPACING`. */
  spacing: number;
  /** Replaces `#` in the block and shadow fonts. Only its first character is used. */
  fill: string;
  /** Draw each letter with itself — an H made of H's. Overrides `fill`. */
  selfFill: boolean;
  /** The shadow font's shadow character. Only its first character is used. */
  shadow: string;
}

export const DEFAULT_ASCII_OPTIONS: AsciiArtOptions = {
  font: "block",
  spacing: 1,
  fill: "#",
  selfFill: false,
  shadow: "/",
};

export interface AsciiArtResult {
  /** The art, rows joined with "\n". Empty when there is nothing to draw. */
  text: string;
  /** The widest row, in characters. */
  width: number;
  /** Total rows of output, blank separator rows included. */
  rows: number;
  /** Characters the font has no glyph for, each listed once. */
  missing: string[];
  /** True when the input was longer than `MAX_INPUT_CHARACTERS` and was cut. */
  truncated: boolean;
}

const FILLED_CELL = "#";
const COMBINING_MARK = /\p{M}/gu;
const LINE_BREAK = /\r\n|\r|\n/;
const TRAILING_SPACE = /\s+$/;

/** Typographic punctuation the fonts draw with their ASCII equivalents. */
const TYPOGRAPHIC: Readonly<Record<string, string>> = {
  "‘": "'",
  "’": "'",
  "‚": ",",
  "“": '"',
  "”": '"',
  "„": '"',
  "–": "-",
  "—": "-",
  "−": "-",
  "…": "...",
  "\t": " ",
  " ": " ",
};

const FONTS: Readonly<Record<Exclude<AsciiFontId, "shadow">, AsciiFont>> = {
  block: BLOCK_GLYPHS,
  slim: SLIM_GLYPHS,
  small: SMALL_GLYPHS,
};

/**
 * The font keys that draw one typed character: curly quotes and dashes become their
 * ASCII forms, accents are dropped (é → E), and the result is upper-cased, which also
 * turns ß into SS. An empty list means the character cannot be drawn.
 */
export function glyphKeys(character: string, font: AsciiFont): string[] {
  const plain = (TYPOGRAPHIC[character] ?? character)
    .normalize("NFD")
    .replace(COMBINING_MARK, "")
    .toUpperCase();
  const keys = [...plain];
  if (keys.length === 0 || !keys.every((key) => key in font)) {
    return [];
  }
  return keys;
}

/**
 * The block glyph with a drop shadow: every filled cell is drawn with `fill` and casts
 * `shadow` one row down and one column right, wherever that cell is empty. The result
 * is one row and one column larger than the glyph.
 */
export function shadowGlyph(
  glyph: readonly string[],
  fill: string,
  shadow: string
): string[] {
  const width = glyph[0]?.length ?? 0;
  const filled = (row: number, column: number) =>
    glyph[row]?.[column] === FILLED_CELL;
  const rows: string[] = [];
  for (let row = 0; row <= glyph.length; row++) {
    let line = "";
    for (let column = 0; column <= width; column++) {
      if (filled(row, column)) {
        line += fill;
      } else if (filled(row - 1, column - 1)) {
        line += shadow;
      } else {
        line += " ";
      }
    }
    rows.push(line);
  }
  return rows;
}

/**
 * The first user-perceived character of a field, or `fallback` when it is empty — a
 * fill of "ab" would make every cell two columns wide and shear the letters apart.
 */
export function singleCharacter(value: string, fallback: string): string {
  const [first] = segmentGraphemes(value.trim());
  return first ?? fallback;
}

function baseFont(id: AsciiFontId): AsciiFont {
  return id === "shadow" ? BLOCK_GLYPHS : FONTS[id];
}

function drawGlyph(
  key: string,
  font: AsciiFont,
  options: AsciiArtOptions
): string[] {
  const glyph = font[key] ?? [];
  if (!FILLED_FONTS.has(options.font)) {
    return [...glyph];
  }
  const fill = options.selfFill
    ? key
    : singleCharacter(options.fill, FILLED_CELL);
  if (options.font === "shadow") {
    return shadowGlyph(glyph, fill, singleCharacter(options.shadow, " "));
  }
  return glyph.map((row) => row.replaceAll(FILLED_CELL, fill));
}

function renderLine(
  line: string,
  options: AsciiArtOptions,
  missing: Set<string>
): string[] {
  const font = baseFont(options.font);
  const glyphs: string[][] = [];
  for (const character of line) {
    const keys = glyphKeys(character, font);
    if (keys.length === 0) {
      missing.add(character);
      continue;
    }
    for (const key of keys) {
      glyphs.push(drawGlyph(key, font, options));
    }
  }
  if (glyphs.length === 0) {
    return [];
  }
  const gap = " ".repeat(Math.min(Math.max(options.spacing, 0), MAX_SPACING));
  const height = glyphs[0].length;
  const rows: string[] = [];
  for (let row = 0; row < height; row++) {
    rows.push(
      glyphs
        .map((glyph) => glyph[row] ?? "")
        .join(gap)
        .replace(TRAILING_SPACE, "")
    );
  }
  return rows;
}

/** Draw `input` as ASCII art. Blank lines in the input are skipped. */
export function renderAsciiArt(
  input: string,
  options: AsciiArtOptions
): AsciiArtResult {
  const characters = [...input];
  const truncated = characters.length > MAX_INPUT_CHARACTERS;
  const text = truncated
    ? characters.slice(0, MAX_INPUT_CHARACTERS).join("")
    : input;
  const missing = new Set<string>();
  const banners: string[][] = [];
  for (const line of text.split(LINE_BREAK)) {
    const rows = renderLine(line, options, missing);
    if (rows.some((row) => row.length > 0)) {
      banners.push(rows);
    }
  }
  const output: string[] = [];
  for (const [index, banner] of banners.entries()) {
    if (index > 0) {
      output.push("");
    }
    output.push(...banner);
  }
  return {
    text: output.join("\n"),
    width: Math.max(0, ...output.map((row) => [...row].length)),
    rows: output.length,
    missing: [...missing],
    truncated,
  };
}

/** The art inside a Markdown code fence, so chat apps and READMEs keep it monospaced. */
export function asciiArtCodeBlock(art: string): string {
  return art.length === 0 ? "" : `\`\`\`\n${art}\n\`\`\``;
}
